import { forwardRef, type ReactNode, useEffect, useMemo } from "react";
import { mergeProps } from "react-aria";
import { Link as RouterLink, type LinkProps as RouterLinkProps } from "react-router-dom";
import { AnimatedTooltip } from "widgets";
import { useDOMRef, useResizeObserver, useTooltip } from "widgets/hooks";

import { matchesURL } from "./utils.ts";

export interface LinkProps extends RouterLinkProps {
  forceExternal?: boolean;
  setExternal?: (isExternal: boolean) => void;
  tooltip?: string;
  forceTooltip?: boolean;
}

export const SmartLink = forwardRef<HTMLAnchorElement, LinkProps>(function SmartLink(
  { forceExternal, to, tooltip, forceTooltip, ...props },
  ref,
) {
  const mustBeExternalAnchor = useMemo(() => typeof to === "string" && matchesURL(to) && !!new URL(to).protocol, [to]);
  const isEmail = useMemo(() => typeof to === "string" && to.startsWith("mailto:"), [to]);
  const triggerRef = useDOMRef(ref);
  const rect = useResizeObserver(triggerRef);

  const { triggerProps, tooltipProps, state } = useTooltip(triggerRef, {
    isDisabled: !(!!tooltip && (forceTooltip || rect?.widthOverflow)),
    delay: 300,
    closeDelay: 0,
  });
  const linkProps = mergeProps(props, triggerProps, {
    style: {
      ...triggerProps.style,
      // needed for the ResizeObserver to calculate width
      display: "inline-block",
      maxWidth: "100%",
      verticalAlign: "middle",
      ...props.style,
    },
  });

  const withTooltip = (child: ReactNode) => (
    <>
      {child}
      {!!state.isOpen && (
        <AnimatedTooltip targetRef={triggerRef} domProps={tooltipProps} state={state} nonInteractive>
          {tooltip}
        </AnimatedTooltip>
      )}
    </>
  );

  useEffect(() => {
    props.setExternal?.(forceExternal === true || mustBeExternalAnchor);
  }, [forceExternal, mustBeExternalAnchor, props]);

  if (to == null) {
    return withTooltip(<span ref={triggerRef} {...linkProps} />);
  }

  if (forceExternal === true || mustBeExternalAnchor || isEmail) {
    if (mustBeExternalAnchor) {
      return withTooltip(
        <a href={to.toString()} {...linkProps} target="_blank" rel="noopener noreferrer" ref={triggerRef} />,
      );
    }

    if (isEmail) {
      return withTooltip(<a href={to.toString()} {...linkProps} ref={ref} />);
    }

    return withTooltip(
      <RouterLink {...linkProps} to={to} target="_blank" rel="noopener noreferrer" ref={triggerRef} />,
    );
  }

  return withTooltip(<RouterLink {...linkProps} to={to} ref={triggerRef} onClick={(e) => e.stopPropagation()} />);
});
